<template>
  <app-layout>
    <template #header>
      <AppHead title="Create Customer" />
      <h2 class="title">Create Customer</h2>
      <flash-messages />
    </template>

    <form @submit.prevent="store" class="mt-3">
      <div class="row">
        <div class="col-12 col-lg-6 mb-3 mb-lg-0">
          <card class="h-100">
            <div class="form-row">
              <div class="form-group col-12 col-lg-6">
                <jet-label class="mb-1" for="first_name" value="First name" />
                <jet-input id="first_name"  type="text" class="form-control" v-model="form.first_name" />
                <jet-input-error :message="form.errors.first_name" class="mt-2" />
              </div>
              <div class="form-group col-12 col-lg-6">
                <jet-label class="mb-1" for="last_name" value="Last name" />
                <jet-input id="last_name"  type="text" class="form-control" v-model="form.last_name" />
                <jet-input-error :message="form.errors.last_name" class="mt-2" />
              </div>
            </div>
            <div class="form-group">
              <jet-label class="mb-1" for="email" value="Email" />
              <jet-input id="email"  type="text" class="form-control" v-model="form.email" />
              <jet-input-error :message="form.errors.email" class="mt-2" />
            </div>
            <div class="form-group">
              <jet-label class="mb-1" for="phone_number" value="Phone" />
              <jet-input id="phone_number"  type="text" class="form-control" v-model="form.phone_number" />
              <jet-input-error :message="form.errors.phone_number" class="mt-2" />

              <div class="form-check mt-2">
                <jet-checkbox id="allow_sms"  class="form-check"  v-model:checked="form.allow_sms" />
                <jet-label for="allow_sms" value="Allow SMS to this number" />
              </div>
            </div>
          </card>
        </div>

        <div class="col-12 col-lg-6">
          <card>
            <div class="form-check">
              <input class="form-check-input" type="radio" name="invite" id="invite_send" v-model="invite" :value="true" @change="this.form.reset('password', 'password_confirmation')">
              <label class="form-check-label" for="invite_send">
                Send User Invitation
              </label>
            </div>
            <div class="form-check mb-3">
              <input class="form-check-input" type="radio" name="invite" id="invite_temporary_password" v-model="invite" :value="false">
              <label class="form-check-label" for="invite_temporary_password">
                Set Temporary Password
              </label>
            </div>
            <div v-if="!invite">
              <div class="form-row mb-3">
                <div class="col-12 mb-2">
                  <jet-input id="password" type="password" class="form-control" placeholder="Password" v-model="form.password" required autocomplete="new-password" />
                  <jet-input-error :message="form.errors.password" class="mt-2" />
                </div>

                <div class="col-12">
                  <jet-input id="password_confirmation" type="password" class="form-control" placeholder="Confirm Password" v-model="form.password_confirmation" required autocomplete="new-password" />
                </div>
              </div>
            </div>

            <div class="form-group mb-0">
              <jet-label for="starting_account_balance" value="Starting Account Balance" />
              <AmountInput v-model="form.starting_account_balance" inputId="starting_account_balance" bottomMargin="mb-1" />
              <jet-input-error :message="form.errors.starting_account_balance" class="mt-2" />

              <p class="text-secondary text-size-small mt-2 mb-0">Set a starting account balance for this customer. Once a payment has been made towards this amount, it can no longer be changed.</p>
            </div>
          </card>

          <card title="Location" icon="fas fa-map-marker-alt" class="mt-3">
            <select id="location_id" class="form-control" v-model="form.location_id">
              <option disabled>Select Location</option>
              <option :value="null">Unassigned</option>
              <option v-for="address in this.locations" :key="address.id" :value="address.id">
                {{ address.company_name }} {{ address.address1 }} {{ address.address2 }} {{ address.city }}, {{ address.state }} {{ address.postcode }}
              </option>
            </select>
            <jet-input-error :message="form.errors.location_id" class="mt-2" />
            <p v-if="form.company_name" class="mb-0 mt-2 badge badge-primary pt-2 pb-1"><i class="fas fa-pen mr-2"></i>{{ form.company_name }} {{ form.address1 }} {{ form.address2 }} {{ form.city }}, {{ form.state }} {{ form.postcode }}</p>
            <p role="button" data-toggle="modal" data-target="#locationModal" class="mb-0 mt-2 text-primary" @click="openNewLocation"><i class="fas fa-plus mr-2"></i>New Location</p>
          </card>
        </div>

        <jet-button btnStyle="success" icon="fas fa-user" :disable="form.processing" class="mt-4 mx-3" type="submit">Create User</jet-button>
      </div>
    </form>

    <AddNewLocation
      :states="states"
      :form="form"
      @saveLocation="saveLocation"
    />
  </app-layout>
</template>

<script>
  import AppLayout from '@/Layouts/AppLayout'
  import FileInput from '@/Shared/FileInput'
  import TextInput from '@/Shared/TextInput'
  import SelectInput from '@/Shared/SelectInput'
  import FlashMessages from '@/Shared/FlashMessages'

  import JetButton from '@/Jetstream/Button'
  import JetFormSection from '@/Jetstream/FormSection'
  import JetInput from '@/Jetstream/Input'
  import JetCheckbox from '@/Jetstream/Checkbox'
  import JetInputError from '@/Jetstream/InputError'
  import JetLabel from '@/Jetstream/Label'
  import JetActionMessage from '@/Jetstream/ActionMessage'
  import AmountInput from '@/Shared/AmountInput'
  import AddNewLocation from '@/Components/Modals/AddNewLocation'

  export default {
    components: {
      AppLayout,
      FileInput,
      SelectInput,
      TextInput,
      JetActionMessage,
      JetButton,
      JetFormSection,
      JetInput,
      JetCheckbox,
      JetInputError,
      JetLabel,
      FlashMessages,
      AddNewLocation,
      AmountInput
    },
    remember: 'form',
    props: {
      roles: Object,
      locations: Array,
      location_id: [Number, String],
      states: Array
    },
    data() {
      return {
        invite: true,
        form: this.$inertia.form({
          company_name: null,
          first_name: null,
          last_name: null,
          email: null,
          role_id: null,
          location_id: this.location_id,
          address1: null,
          address2: null,
          location_visit_on_day: '',
          city: null,
          state: null,
          postcode: null,
          phone_number: null,
          allow_sms: true,
          password: null,
          password_confirmation: null,
          starting_account_balance: null
        }),
      }
    },
    methods: {
      store() {
        this.form.post(this.route('users.storetu'))
      },
      openNewLocation() {
        this.form.location_id = ''
        $('#locationModal').modal('show');
      },
      saveLocation() {
        this.form.location_id = this.location_id
      }
    },
  }
</script>
